import * as React from 'react';
import { useState } from 'react';
import Panel from '../../../components/Panel';
import { useHomeTabList } from '../../../hooks/home/useHomeTabList';
import HomeTabListTable from './HomeTabListTable';
import HomeTabListHeader from './HomeTabListHeader';
import { useCreateHomeTab } from './hooks/useCreateHomeTab';
import HomeTabListFormModal from './HomeTabListFormModal';
import toastMsg from '../../../lib/toastMsg';
import { extractErrorMessage } from '../../../lib/utils/checkErrorResponse';
import { useEditHomeTab } from './hooks/useEditHomeTab';
import HomeTabListFooter from './HomeTabListFooter';
import { useEditHomeTabListMutate } from '../../../hooks/queries/home/useEditHomeTabListMutate';
import { HOME_TAB_LINK_TYPE_V2, HOME_TAB_STATUS_TYPE_V2 } from '../../../constants/service/app/homeConstantsV2';
import { HomeTab, HomeTabCreateRequest } from '../../../types/home/tab';
import HomeTabListPreview from './HomeTabListPreview';
import { asyncSimpleModal } from '../../../lib/common';
import moment from '../../../lib/moment';

const HomeTabListWrap = (): JSX.Element => {
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);

  const {
    homeTabList,
    changeHomeTabStatus,
    changeHomeTabListOrder,
    refetchHomeTabList,
    resetHomeTabList,
    isHomeTabListChanged,
  } = useHomeTabList();

  const { mutate: editHomeTabList } = useEditHomeTabListMutate({
    onSuccess: () => {
      toastMsg.success('변경 내용이 저장되었습니다.');
      refetchHomeTabList();
    },
    onError: (err) => toastMsg.error(extractErrorMessage(err)),
  });

  const { isHomeTabCreateModalOpen, openHomeTabCreateModal, closeHomeTabCreateModal, createHomeTab } = useCreateHomeTab(
    {
      onSuccess: () => {
        toastMsg.success('홈 탭이 생성되었습니다.');
        refetchHomeTabList();
      },
      onError: (err) => toastMsg.error(extractErrorMessage(err)),
    },
  );

  const { targetTabId, editHomeTab, changeTargetTabId } = useEditHomeTab({
    onSuccess: () => {
      toastMsg.success('변경 내용이 저장되었습니다.');
      refetchHomeTabList();
    },
    onError: (err) => toastMsg.error(extractErrorMessage(err)),
  });

  const handleEditHomeTabList = async (): Promise<void> => {
    const isConfirm = await asyncSimpleModal({
      type: 'confirm',
      title: '홈 탭 저장',
      message: '변경된 사항을 저장 하시겠습니까?',
    });

    if (isConfirm) editHomeTabList(homeTabList);
  };

  const handleResetHomeTabList = async (): Promise<void> => {
    const isConfirm = await asyncSimpleModal({
      type: 'confirm',
      title: '홈 탭 초기화',
      message: '변경된 사항을 초기화 하시겠습니까?',
    });

    if (isConfirm) resetHomeTabList();
  };

  const isValidTab = (tab: HomeTab): boolean => {
    const currentDate = new Date();
    const isTabInfinite = tab.period.startDate === null && tab.period.endDate === null;
    const isValidPeriod =
      isTabInfinite || moment(currentDate).isBetween(moment(tab.period.startDate), moment(tab.period.endDate));

    return isValidPeriod && tab.status === HOME_TAB_STATUS_TYPE_V2.ACTIVE;
  };

  return (
    <Panel title={<HomeTabListHeader title="홈 탭 화면 구성" onClickCreateTab={openHomeTabCreateModal} />}>
      <HomeTabListTable
        list={homeTabList}
        onChangeTabListOrder={({ list }): void => changeHomeTabListOrder(list)}
        onChangeTabStatus={changeHomeTabStatus}
        onClickEdit={(id): void => changeTargetTabId(id)}
      />
      <HomeTabListFooter
        onSave={handleEditHomeTabList}
        onReset={handleResetHomeTabList}
        onPreview={(): void => setIsPreviewOpen(true)}
        canShowSaveAndReset={isHomeTabListChanged}
      />

      {isHomeTabCreateModalOpen && <HomeTabListFormModal onSubmit={createHomeTab} onClose={closeHomeTabCreateModal} />}
      {targetTabId && (
        <HomeTabListFormModal
          onSubmit={(tab): void => editHomeTab({ ...tab, id: targetTabId })}
          onClose={(): void => changeTargetTabId(null)}
          initialValue={
            homeTabList.find(
              (tab) => tab.linkType !== HOME_TAB_LINK_TYPE_V2.MAIN && tab.id === targetTabId,
            ) as HomeTabCreateRequest
          }
        />
      )}
      {isPreviewOpen && (
        <HomeTabListPreview tabList={homeTabList.filter(isValidTab)} onClose={(): void => setIsPreviewOpen(false)} />
      )}
    </Panel>
  );
};

export default HomeTabListWrap;
